'use client';

import React from 'react';
import type { Locale, TranslationKey, TranslationKeys } from './types';
import { enUS } from './translations/en-US';
import { trTR } from './translations/tr-TR';
import { kkKZ } from './translations/kk-KZ';
import { azAZ } from './translations/az-AZ';
import { getLocale } from './getLocale';

const translations: Record<Locale, TranslationKeys> = {
  'en-US': enUS,
  'tr-TR': trTR,
  'kk-KZ': kkKZ,
  'az-AZ': azAZ,
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string>) => string;
}

const I18nContext = React.createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: React.ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocale] = React.useState<Locale>(() => {
    if (initialLocale) return initialLocale;
    if (typeof window !== 'undefined') return getLocale();
    return 'en-US';
  });

  // The root layout hardcodes <html lang="en"> — keep the document language in
  // sync with the active locale (client-only; guarded against SSR).
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
  }, [locale]);

  const t = React.useCallback(
    (key: TranslationKey, vars?: Record<string, string>): string => {
      let text = translations[locale][key] ?? translations['en-US'][key] ?? key;

      // Replace variables like {message} with actual values
      if (vars) {
        Object.entries(vars).forEach(([varKey, value]) => {
          text = text.replace(new RegExp(`\\{${varKey}\\}`, 'g'), value);
        });
      }

      return text;
    },
    [locale]
  );

  const value = React.useMemo(
    () => ({ locale, setLocale, t }),
    [locale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const context = React.useContext(I18nContext);

  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }

  return context;
}

export function useTranslationOptional() {
  return React.useContext(I18nContext);
}
